<template>
  <div class="p-grid">
    <div class="p-col-12">
      <div class="card">
<!--        icon="ti-files"-->
        <BillingFeatureGate feature="work_templates"
                            variant="page"
                            :title="$t('workAndPartsTemplates')"
                            :description="$t('billing.features.work_templates.tooltip')">
          <Toolbar class="p-mb-4 p-p-2 page-toolbar">
            <template #start>
              <div style="font-size: 1.4em;" class="p-p-2 p-mr-3">{{ $t('workAndPartsTemplates') }}</div>
<!--              <div class="p-p-1">-->
                <AddNewButton v-show="canModerate"
                              :loading="!allDataLoaded && !loadingError"
                              :disabled="!allDataLoaded || loadingError"
                              :label="$t('Add')"
                              @click="addNewItem"/>
<!--              </div>-->
            </template>

            <template #end>
              <div class="p-m-1">
                <SearchInput :disabled="dataTableIsLoading || loadingError"
                             @change-processed-search-value="changeProcessedSearchValue"
                             v-model="searchData"
                             @show-spinner="isLoading = true"/>
              </div>
            </template>
          </Toolbar>

          <Spinner v-if="isLoading"/>
          <div v-else-if="!isLoading && !items?.length" class="empty-page">
            {{ $t('No data available') }}
          </div>

          <DesktopDataTable v-else
                            :showHeader="false"
                            :dataTableIsLoading="dataTableIsLoading"
                            :itemsArrayIsEmpty="itemsArrayIsEmpty"
                            :sort="sort"
                            :showSearchInput="false"
                            :actionButtonsCount="actionButtonsCount"
                            @sort-items="sortItems"
                            :headers="[
                              {name: 'name', title: $t('Name'), minWidth: '140px', sortable: true},
                              {name: 'description', title: $t('Description'), minWidth: '220px', sortable: false},
                              {name: 'rows', title: $t('workTemplateRowsCountLabel'), sortable: false},
                              {name: 'amount', title: $t('Sum without tax'), sortable: false},
                              {name: 'created_at', title: $t('Created'), sortable: true},
                            ]">
            <template v-slot:body>
              <tr v-for="item of items"
                  :key="item.id"
                  :class="{'updated-item': isItemUpdated(item.id)}">
                <td>
                  <span v-if="item.name">{{ item.name }}</span>
                  <i v-else class="ti-minus"/>
                </td>

                <td>
                  <span v-if="item.description" class="pre-line">{{ item.description }}</span>
                  <i v-else class="ti-minus"/>
                </td>

                <td>
                  <span v-if="item.positions?.length">{{ item.positions.length }}</span>
                  <i v-else class="ti-minus"/>
                </td>

                <td class="table-data number-cell">
                  {{ formatCurrency(calculateTemplateSubtotal(item)) }}
                </td>

                <td class="table-date-line">
                  <span v-if="item.created_at">
                    {{ formatBy(item.created_at, item.createdBy) }}
                  </span>
                  <i v-else class="ti-minus"/>
                </td>

                <td v-show="actionButtonsCount">
                  <div class="actions">
                    <EditButton v-show="canModerate"
                                :disabled="dataTableIsLoading || !allDataLoaded"
                                @click="editItem(item)"/>
                    <DeleteButton v-show="canModerate"
                                  :disabled="dataTableIsLoading"
                                  @click="confirmItemDelete(item)"/>
                  </div>
                </td>
              </tr>
            </template>
          </DesktopDataTable>

          <OwnPaginator v-show="!itemsArrayIsEmpty"
                        :rows="pagination.perPage"
                        :currentPage="pagination.currentPage - 1"
                        :totalPages="pagination.pageCount"
                        :totalRecords="pagination.totalCount"
                        :rowsPerPageOptions="[5,10,25,50,100,200]"
                        :pageLinkSize="11"/>

          <TotalRecordsData :pagination="pagination" :itemsArrayIsEmpty="itemsArrayIsEmpty"/>

          <WorkTemplateModal v-if="showModals"
                             :visible="itemModal"
                             :item="item"
                             :services="services"
                             :suppliers="suppliers"
                             :warehouseItems="warehouseItems"
                             @update-items="updateItems"
                             @close="closeItemModal"/>

          <ConfirmDeleteModal :visible="confirmItemDeleteModal"
                              :modalDataIsLoading="modalDataIsLoading"
                              @close="closeConfirmItemDeleteModal"
                              @delete-item="deleteItem"
                              :data="itemToDeleteData"/>
        </BillingFeatureGate>
      </div>
    </div>
  </div>
</template>

<script>
import httpClient from '@/services/http.services'
import WorkTemplateModal from '@/pages/data_directory/components/WorkTemplateModal'
import datatableMixins from '@/mixins/datatableMixins'
import formatMixins from '@/mixins/formatMixins'
import httpMixins from '@/mixins/httpMixins'
import settings from '@/settings'
import showErrorsMixins from '@/mixins/showErrorsMixins'
import BillingFeatureGate from '@/components/billing/BillingFeatureGate'
import billingFeaturesMixins from '@/mixins/billingFeaturesMixins'

export default {
  name: 'WorkTemplates',
  mixins: [datatableMixins, showErrorsMixins, formatMixins, httpMixins, billingFeaturesMixins],
  components: {
    WorkTemplateModal,
    BillingFeatureGate,
  },
  data() {
    return {
      fieldsExpand: 'createdBy,positions',
      apiLink: 'work-template',
      deleteItemUrl: 'work-template/delete',
      routePath: '/work-templates',
      sort: {
        column: 'id',
        direction: '-',
      },
    }
  },
  watch: {
    itemModal(value) {
      if (value) {
        this.checkModalDataIsFresh()
      }
    },
    canUseWorkTemplates(allowed, wasAllowed) {
      if (allowed && !wasAllowed) {
        this.$nextTick(() => {
          this.checkRouteQuery()
          this.getPageData()
        })
        return
      }

      if (!allowed && wasAllowed) {
        this.resetWorkTemplatesState()
      }
    },
  },
  mounted() {
    this.scrollToTop()
    if (settings.autoLogoutTimeout && this.$store.state.logoutTimestampIsOverdue) return false

    if (this.canUseWorkTemplates) {
      this.checkRouteQuery()
      this.getPageData()
    }
  },
  methods: {
    async getPageData() {
      if (!this.canUseWorkTemplates) return
      if (!this.userCanGetData || this.fetchInFlight) return

      this.getItems()
      this.getModalDataOnMounted()
    },
    resetWorkTemplatesState() {
      this._abortors?.workTemplates?.abort()

      this.items = []
      this.itemModal = false
      this.confirmItemDeleteModal = false
      this.isLoading = false
      this.dataTableIsLoading = false
      this.loadingError = false
      this.itemsReceivedTimestamp = null
      this.disableEditButton = false
    },
    calculateTemplateSubtotal(item) {
      return (item.positions || [])
          .filter(position => Number(position.status ?? 1) === 1)
          .reduce((sum, position) => {
            const qty = this.toNumber(position.qty)
            const sellPrice = this.toNumber(position.sell_price)
            const discount = this.toNumber(position.discount)

            const rowSum = (sellPrice - (sellPrice * discount / 100)) * qty

            return sum + rowSum
          }, 0)
    },

    async getItems(showSpinner = false) {
      if (!this.canUseWorkTemplates || !this.userCanGetData || this.fetchInFlight) return

      this.$store.commit('SET_FETCH_IN_FLIGHT', true)
      this.dataTableIsLoading = true
      this.disableEditButton = true
      this.itemsReceivedTimestamp = Date.now()

      if (showSpinner) {
        this.isLoading = true
      }

      const ctrl = this.setController?.('workTemplates')

      try {
        const {status, data, headers} = await httpClient({
          url: this.apiLink,
          params: {
            page: this.pagination.currentPage,
            'per-page': this.pagination.perPage,
            sort: this.sort.direction + this.sort.column,
            search: this.processedSearchValue ?? null,
            expand: this.fieldsExpand,
          },
          signal: ctrl?.signal,
        })

        if (!this.canUseWorkTemplates) return

        if (status === 200 && data && Array.isArray(data)) {
          const hasPaginationHeaders = !!headers['x-pagination-total-count']

          if (hasPaginationHeaders) {
            this.pagination.totalCount = +headers['x-pagination-total-count']
            this.pagination.pageCount = +headers['x-pagination-page-count']
          } else {
            this.pagination.totalCount = data.length
            this.pagination.pageCount = 1
          }

          this.items = data

          if (!data.length) {
            this.pagination.pageCount = 1
            this.pagination.currentPage = 1
          }
        } else {
          this.items = []
          this.loadingError = true
          this.showUnknownError()
        }
      } catch (err) {
        if (!this.canUseWorkTemplates) return
        this.items = []
        this.loadingError = true
        this.showError(err)
      } finally {
        this.$store.commit('SET_FETCH_IN_FLIGHT', false)
        this.isLoading = false
        this.dataTableIsLoading = false
        this.disableEditButton = false
        this.itemsReceivedTimestamp = null

        if (this._abortors?.workTemplates === ctrl) {
          this._abortors.workTemplates = null
        }
      }
    },
  },
  computed: {
    canModerate() {
      return this.$store.getters.isAccountantOrHigher
    },
    allDataLoaded() {
      return !!(this.services && this.suppliers && this.warehouseItems)
    },
    actionButtonsCount() {
      return this.canModerate ? 2 : 0
    },
    itemToDeleteData() {
      return this.itemToDelete?.name ?? null
    },
  },
}
</script>

<style scoped lang="scss">
td {
  height: 44px
}
</style>
